/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, OnInit } from '@angular/core';
import { CurrenciesService } from '@app/settings/currencies/currencies.service';
import { Logger } from '@app/core';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { ApiQuery } from '@app/core/http/api-query';
import { Currency } from '@app/settings/currencies/currency.model';
import { CurrenciesFormService } from '@app/settings/currencies/currencies-form.service';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { ToastrService } from 'ngx-toastr';
import { KendoGridComponent } from '@app/shared/kendo/kendo-grid.component';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { environment } from '@env/environment';
import { DataSet } from '@app/shared/kendo/data-set';
import { CustomKendoFilter } from '@app/shared/kendo/custom-kendo-filter';
import { AccessService } from '@app/core/access/access.service';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';

const log = new Logger('CurrenciesListComponent');

@Component({
  selector: 'app-currencies-list',
  templateUrl: './currencies-list.component.html',
  styleUrls: ['./currencies-list.component.scss'],
})
export class CurrenciesListComponent extends KendoGridComponent implements OnInit {
  currenciesResponse: PaginatedResponse;
  data: GridDataResult;
  state: DataStateChangeEvent;
  apiQuery: ApiQuery = new ApiQuery();
  env = environment;

  constructor(
    private currenciesService: CurrenciesService,
    private currenciesFormService: CurrenciesFormService,
    private toastr: ToastrService,
    protected kendoGridService: KendoGridService,
    public accessService: AccessService
  ) {
    super(kendoGridService);
  }

  ngOnInit() {
    this.state = new DataSet().addSort('order_index', 'desc');

    const storageFilter = CustomKendoFilter.getStateFromStorage('CurrenciesListComponent');
    if (storageFilter) {
      this.state = storageFilter;
    }

    this.loadData(this.state);
  }

  /**
   * Open currencies form
   */
  add() {
    this.currenciesFormService.open().then(() => {
      this.loadData();
    });
  }

  /**
   *
   * @param currency Currency object
   */
  edit(currency: Currency) {
    this.currenciesFormService.open(currency);
  }

  /**
   * Update rates
   */
  updateRates() {
    this.currenciesService.updateRates().subscribe(() => {
      this.toastr.success('Currency rates updated');
      this.loadData();
    });
  }

  /**
   *
   * @param currency Currency object
   */
  delete(currency: Currency) {
    this.currenciesService.destroy(currency.id).subscribe(() => {
      this.loadData(this.state);
    });
  }

  /**
   * Load data from API
   */
  loadData(state: DataStateChangeEvent = null) {
    this.state = state;
    this.apiQuery.setDataSetFilters(state);

    this.currenciesService.index(this.apiQuery).subscribe((response: PaginatedResponse) => {
      this.currenciesResponse = response;
      log.debug(response.data);
      this.data = response.getGridData();
    });
    CustomKendoFilter.pushStateInStorage('CurrenciesListComponent', this.state);
  }

  clearFilters() {
    this.apiQuery = new ApiQuery();
    this.loadData(new DataSet());
  }
}
